import React from "react";

// ─────────────────────────────────────────────────────────────
// Shared booking form primitives — Onyx + Bronze themed
// ─────────────────────────────────────────────────────────────

export function Section({ title, children }) {
  return (
    <fieldset className="space-y-6 border-t border-white/10 pt-10 first:border-t-0 first:pt-0">
      <legend className="font-overline text-[0.7rem] tracking-[0.22em] text-[#DE9E48]">
        — {title}
      </legend>
      <div className="grid md:grid-cols-2 gap-5">{children}</div>
    </fieldset>
  );
}

export function FullWidth({ children }) {
  return <div className="md:col-span-2">{children}</div>;
}

const inputClasses =
  "w-full bg-stone-950 border border-white/10 rounded-lg px-4 py-3 text-stone-100 font-body text-sm placeholder:text-stone-600 focus:border-[#DE9E48] focus:outline-none transition-colors";

export function TextField({ label, value, onChange, type = "text", required, placeholder, testId, fullWidth, autoComplete, min, max }) {
  const field = (
    <label className="block">
      <span className="font-overline text-[0.65rem] text-stone-400 tracking-[0.18em] block mb-2">
        {label}
        {required && <span className="text-[#DE9E48] ml-1">*</span>}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        min={min}
        max={max}
        className={inputClasses}
      />
    </label>
  );
  return fullWidth ? <FullWidth>{field}</FullWidth> : field;
}

export function TextAreaField({ label, value, onChange, required, placeholder, rows = 4, testId }) {
  return (
    <FullWidth>
      <label className="block">
        <span className="font-overline text-[0.65rem] text-stone-400 tracking-[0.18em] block mb-2">
          {label}
          {required && <span className="text-[#DE9E48] ml-1">*</span>}
        </span>
        <textarea
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          rows={rows}
          className={inputClasses + " resize-y leading-relaxed"}
        />
      </label>
    </FullWidth>
  );
}

export function RadioField({ label, value, onChange, options, required, testId, fullWidth = true }) {
  const node = (
    <div>
      <p className="font-overline text-[0.65rem] text-stone-400 tracking-[0.18em] mb-3">
        {label}
        {required && <span className="text-[#DE9E48] ml-1">*</span>}
      </p>
      <div className="flex flex-wrap gap-2" data-testid={testId}>
        {options.map((opt) => {
          const checked = value === opt;
          return (
            <label
              key={opt}
              data-testid={testId ? `${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
              className={[
                "cursor-pointer px-4 py-2 rounded-full border font-body text-sm transition-all select-none",
                checked
                  ? "bg-[#DE9E48] border-[#DE9E48] text-white"
                  : "border-white/15 text-stone-300 hover:border-[#DE9E48]/60 hover:text-stone-100",
              ].join(" ")}
            >
              <input
                type="radio"
                value={opt}
                checked={checked}
                onChange={() => onChange(opt)}
                required={required}
                className="sr-only"
              />
              {opt}
            </label>
          );
        })}
      </div>
    </div>
  );
  return fullWidth ? <FullWidth>{node}</FullWidth> : node;
}

export function CheckboxGroup({ label, values, onChange, options, testId }) {
  const toggle = (opt) => {
    const next = values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt];
    onChange(next);
  };
  return (
    <FullWidth>
      <p className="font-overline text-[0.65rem] text-stone-400 tracking-[0.18em] mb-3">
        {label}
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid={testId}>
        {options.map((opt) => {
          const checked = values.includes(opt);
          return (
            <label
              key={opt}
              data-testid={testId ? `${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
              className={[
                "cursor-pointer flex items-center gap-3 px-4 py-2.5 rounded-lg border font-body text-sm transition-all select-none",
                checked
                  ? "bg-[#DE9E48]/15 border-[#DE9E48] text-stone-100"
                  : "border-white/10 text-stone-300 hover:border-[#DE9E48]/40",
              ].join(" ")}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={[
                  "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                  checked ? "bg-[#DE9E48] border-[#DE9E48]" : "border-white/25",
                ].join(" ")}
              >
                {checked && (
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L7 12L13 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="leading-tight">{opt}</span>
            </label>
          );
        })}
      </div>
    </FullWidth>
  );
}

export function AcknowledgmentCheckbox({ checked, onChange, testId, children }) {
  return (
    <FullWidth>
      <label className="flex items-start gap-3 cursor-pointer bg-stone-900/50 border border-white/10 rounded-xl px-5 py-4">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
          className="sr-only"
        />
        <span
          aria-hidden
          className={[
            "w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5",
            checked ? "bg-[#DE9E48] border-[#DE9E48]" : "border-white/30",
          ].join(" ")}
        >
          {checked && (
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M3 8L7 12L13 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span className="font-body text-sm text-stone-300 leading-relaxed">{children}</span>
      </label>
    </FullWidth>
  );
}

export const ACKNOWLEDGMENT_TEXT =
  "I acknowledge that Encore Theatre & Gallery reserves the right to decline bookings that do not align with its mission, values, or operational standards.";

/**
 * Hidden honeypot input. Off-screen for sighted users, hidden from AT.
 * Bots that auto-fill every input will trip it; the backend silently rejects.
 */
export function Honeypot({ value, onChange, testId = "hp-url" }) {
  return (
    <div
      aria-hidden="true"
      style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}
    >
      <label htmlFor={testId}>Website</label>
      <input
        id={testId}
        data-testid={testId}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
